import type { AgenticLogicRuleSettings } from "@intelligems/ig-types/dtos/agentic-logic/rules";

import { sameTemplateParams } from "../template-config/templateConfigJson";
import { sameRuleSettings } from "./ruleSettings";

/**
 * Whether the rule's saved recommendation settings differ from the copy frozen
 * onto the version that is serving. Settings autosave onto the rule row and
 * take effect at the next publish, so this gap is real staleness on the
 * storefront.
 *
 * Two readers. The template rule's canvas uses it to choose between the graph
 * frozen onto the serving version and one derived from the saved
 * configuration. `useUnpublishedRuleChanges` uses it to decide whether to
 * narrate the gap at all.
 *
 * Narrating it is back, and only because of one writer. Every control on a rule
 * page saves and deploys in one act, so a gap opened there closes in the same
 * breath. Atlas does not: it writes the rule row from the chat and deliberately
 * stops, so an edit to a rule that is already serving is a standing condition
 * the merchant has to be told about and resolve with Save.
 *
 * `null` active settings means nothing is serving yet (never published, or the
 * only publish failed) — not a stale storefront, so not this predicate's story.
 */
export const hasUnpublishedRuleSettings = (
  saved: AgenticLogicRuleSettings,
  active: AgenticLogicRuleSettings | null
): boolean => active !== null && !sameRuleSettings(saved, active);

/**
 * Whether the rule's saved template configuration differs from the params
 * frozen onto the version that is serving — the same disclosure the settings
 * comparison above exists for, on the other half of a template rule's input.
 *
 * Reachable the moment the version history offers rollback: rollback moves the
 * version pointer and never rewrites the rule row, so the configuration on
 * screen can stop being the configuration that serves.
 *
 * A `null` active copy means nothing is serving yet — not a stale storefront,
 * so not this predicate's story.
 */
export const hasUnpublishedRuleConfig = (
  saved: Record<string, unknown> | null,
  active: Record<string, unknown> | null
): boolean => {
  // One identity rule for template params, owned by the module that owns the
  // params ⇄ text canonicalization.
  return active !== null && !sameTemplateParams(saved, active);
};
